import { Link } from "react-router-dom";
import { useState } from "react";
import {
  ShieldCheck, LineChart, ChevronDown, ArrowRight, UserPlus, Link2, Activity,
  CalendarClock, Wallet, Lock, Percent, DollarSign, AlertTriangle, Check,
} from "lucide-react";

const TERMS = [
  { icon: DollarSign, label: "Minimum Equity", value: "$1,000", note: "In your own MT5 account" },
  { icon: Percent, label: "Profit Share", value: "50 / 50", note: "On eligible net profit only" },
  { icon: CalendarClock, label: "Settlement", value: "Every Friday", note: "Weekly performance cycle" },
  { icon: ShieldCheck, label: "Custody", value: "None", note: "Funds stay at your broker" },
];

const STEPS = [
  { n: "01", icon: UserPlus, title: "Apply & Verify", text: "Create your account and complete your profile. Managed copy-trading requires a minimum of USD $1,000 equity." },
  { n: "02", icon: Link2, title: "Connect MT5", text: "Securely link your MetaTrader 5 account. Credentials are AES-encrypted and your password is never exposed." },
  { n: "03", icon: Activity, title: "We Trade XAUUSD", text: "Our managed gold strategy executes XAUUSD trades directly on your connected account. You keep full ownership." },
  { n: "04", icon: CalendarClock, title: "Weekly Settlement", text: "We share 50% of eligible net profit, settled every Friday. Flat or losing weeks carry no performance fee." },
];

const PILLARS = [
  { icon: ShieldCheck, title: "Non-Custodial by Design", text: "ChartzFx never holds your capital. Your funds stay in your own MT5 broker account at all times — deposits and withdrawals happen directly with your broker." },
  { icon: LineChart, title: "Full Transparency", text: "Live equity, drawdown, open positions and trade history sync straight from your MT5 account to your dashboard." },
  { icon: Percent, title: "Aligned Incentives", text: "We only earn when you do. No platform fee, no monthly subscription — a 50/50 split on eligible net profit, settled weekly." },
  { icon: Lock, title: "Security First", text: "TLS in transit, encryption at rest, and investor-password handling that never surfaces your credentials in any API or report." },
];

const FAQS = [
  { q: "How does ChartzFx make money?", a: "We operate on a 50/50 profit-share model. We earn 50% of eligible net profit, settled every Friday. Flat or losing weeks carry no performance fee — there is no platform fee or monthly subscription." },
  { q: "What is the minimum to start?", a: "You need a minimum of USD $1,000 in equity in your own MT5 trading account. Accounts below this threshold are not eligible for managed copy-trading." },
  { q: "What does ChartzFx trade?", a: "Our managed strategy focuses on XAUUSD (gold). Trades are executed directly on your connected MT5 account." },
  { q: "Do you hold my funds?", a: "Never. ChartzFx is non-custodial. All capital stays in your own MT5 broker account. Deposits and withdrawals happen directly with your broker — we only execute trades on your authorized account." },
  { q: "Which brokers are supported?", a: "Any MT5 broker. You connect with your MT5 login, investor password and server name. Popular choices include IC Markets, Pepperstone, Exness, XM and Vantage." },
  { q: "Is my MT5 password safe?", a: "Yes. Credentials are AES-encrypted at rest, transmitted over TLS, and never exposed through any API, report or screen." },
  { q: "Can I stop at any time?", a: "Yes. You can disconnect your account at any time and withdraw directly from your broker — you retain full ownership and control throughout." },
  { q: "Are returns guaranteed?", a: "No. Trading gold on margin carries significant risk and you may lose capital. ChartzFx does not guarantee any profit, return or protection of capital. Past performance is not indicative of future results." },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card-dark px-6 py-4 cursor-pointer" data-testid={`faq-item-${q.slice(0, 12).replace(/\s/g, "-").toLowerCase()}`} onClick={() => setOpen(!open)}>
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm sm:text-base text-gray-100">{q}</p>
        <ChevronDown size={18} className={`text-[#00F0FF] shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </div>
      {open && <p className="text-sm text-gray-400 mt-3 leading-relaxed">{a}</p>}
    </div>
  );
}

export default function Landing() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden grid-noise">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#7000FF]/20 blur-[140px]" />
        <div className="absolute top-40 -left-40 w-[400px] h-[400px] rounded-full bg-[#FFD600]/10 blur-[120px]" />
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 sm:pt-28 relative">
          <div className="max-w-3xl fade-up">
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs text-[#FFD600] mb-8 tracking-widest uppercase" data-testid="hero-badge">
              <Activity size={14} /> Managed XAUUSD copy-trading · Non-custodial
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium text-white leading-[1.05]" data-testid="hero-heading">
              Professional gold trading,<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FFD600] via-[#B074FF] to-[#00F0FF]">copied to your own MT5 account</span>
            </h1>
            <p className="text-base text-gray-400 mt-6 max-w-xl leading-relaxed">
              ChartzFx connects your MetaTrader 5 account to our managed XAUUSD strategy. You keep your capital at your own broker — we execute the trades, you stay in full control.
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link data-testid="hero-get-started-button" to="/register" className="btn-primary px-7 py-3 text-sm font-medium inline-flex items-center gap-2">
                Get Started <ArrowRight size={16} />
              </Link>
              <Link data-testid="hero-login-button" to="/login" className="btn-outline px-7 py-3 text-sm inline-flex items-center">Sign In</Link>
            </div>
            <p className="text-xs text-gray-500 mt-6" data-testid="hero-terms-line">
              Minimum equity <span className="text-gray-300">USD $1,000</span> · <span className="text-gray-300">50/50</span> share of eligible net profit · Settled <span className="text-gray-300">every Friday</span>
            </p>
          </div>
        </div>
      </section>

      {/* KEY TERMS STRIP */}
      <section className="border-y border-white/10 bg-[#0a0a0a]" data-testid="terms-strip">
        <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {TERMS.map((t) => (
            <div key={t.label} data-testid={`term-${t.label.toLowerCase().replace(/\s/g, "-")}`} className="flex items-start gap-3">
              <t.icon size={20} strokeWidth={1.5} className="text-[#FFD600] mt-1 shrink-0" />
              <div>
                <p className="tabular text-xl sm:text-2xl text-white">{t.value}</p>
                <p className="text-sm text-gray-300">{t.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{t.note}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 py-24" data-testid="how-it-works">
        <p className="label-xs mb-3">How it works</p>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-white mb-12 max-w-xl">From application to weekly settlement, in four steps.</h2>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <div key={s.n} data-testid={`step-${s.n}`}
                 className="relative rounded-xl border border-white/10 bg-white/[0.02] backdrop-blur-xl p-6 hover:border-[#FFD600]/40 hover:bg-white/[0.04] transition-all duration-300 group"
                 style={{ animationDelay: `${i * 90}ms` }}>
              <div className="flex items-start justify-between mb-5">
                <span className="tabular text-[10px] tracking-[0.3em] text-[#B074FF] font-medium">{s.n}</span>
                <s.icon size={18} className="text-[#00F0FF] group-hover:scale-110 transition-transform" />
              </div>
              <h3 className="text-white text-base font-medium mb-2">{s.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PROFIT-SHARE TERMS + ILLUSTRATIVE DASHBOARD */}
      <section className="max-w-7xl mx-auto px-6 pb-24 grid lg:grid-cols-2 gap-6 items-stretch" data-testid="profit-share-section">
        <div className="rounded-xl border border-[#FFD600]/25 bg-gradient-to-br from-[#FFD600]/[0.06] via-transparent to-[#7000FF]/[0.05] backdrop-blur-xl p-8 flex flex-col">
          <p className="label-xs mb-5">The arrangement</p>
          <h3 className="text-xl sm:text-2xl text-white font-medium mb-4">A straightforward 50/50 profit share.</h3>
          <ul className="space-y-3 flex-1">
            {[
              "You fund and keep at least USD $1,000 in your own MT5 account.",
              "We execute our managed XAUUSD strategy on your connected account.",
              "Each Friday, eligible net profit for the week is calculated.",
              "You keep 50%; ChartzFx takes 50% of that eligible net profit.",
              "Flat or losing weeks carry no performance fee — we only earn when you profit.",
            ].map((b, i) => (
              <li key={i} data-testid={`arrangement-${i}`} className="flex items-start gap-3 text-sm text-gray-300">
                <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#00E676]/10 text-[#00E676] shrink-0">
                  <Check size={12} strokeWidth={3} />
                </span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-gray-500 mt-6 leading-relaxed">
            "Eligible net profit" means realised trading gains for the settlement week, net of trading costs, after recovering any prior-period losses (high-water mark). It does not include your own deposits.
          </p>
        </div>

        <div className="card-dark p-2 sm:p-3 shadow-[0_0_60px_rgba(255,214,0,0.1)]">
          <div className="rounded-lg bg-[#0a0a0a] border border-white/5 p-6 sm:p-8 h-full">
            <div className="flex items-center justify-between mb-5">
              <p className="label-xs">Illustrative dashboard</p>
              <span className="text-[10px] uppercase tracking-widest text-gray-600">For demonstration only</span>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[["EQUITY", "$12,480", "#00F0FF"], ["OPEN POSITIONS", "2", "#FFD600"], ["SETTLES", "Friday", "#B074FF"]].map(([l, v, c]) => (
                <div key={l} className="glass rounded-lg p-3">
                  <p className="label-xs">{l}</p>
                  <p className="tabular text-lg mt-1" style={{ color: c }}>{v}</p>
                </div>
              ))}
            </div>
            <svg viewBox="0 0 800 160" className="w-full h-32">
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FFD600" stopOpacity="0.22" /><stop offset="100%" stopColor="#FFD600" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d="M0,130 C80,120 120,105 180,108 C260,112 300,80 380,88 C460,95 500,70 560,75 C640,80 700,45 800,55 L800,160 L0,160 Z" fill="url(#g1)" />
              <path d="M0,130 C80,120 120,105 180,108 C260,112 300,80 380,88 C460,95 500,70 560,75 C640,80 700,45 800,55" fill="none" stroke="#FFD600" strokeWidth="2" />
            </svg>
            <p className="text-xs text-gray-600 mt-4">Figures shown are illustrative and do not represent actual or expected performance.</p>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="max-w-7xl mx-auto px-6 pb-24" data-testid="pillars-section">
        <p className="label-xs mb-3">Why ChartzFx</p>
        <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white mb-12 max-w-lg">Built so your capital — and your trust — stay with you.</h2>
        <div className="grid sm:grid-cols-2 gap-5">
          {PILLARS.map((p) => (
            <div key={p.title} className="card-dark card-dark-hover p-6" data-testid={`pillar-${p.title.slice(0, 10).replace(/\s/g, "-").toLowerCase()}`}>
              <p.icon size={22} strokeWidth={1.5} className="text-[#00F0FF] mb-4" />
              <h3 className="text-base text-gray-100 font-medium mb-2">{p.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* RISK DISCLOSURE */}
      <section className="max-w-7xl mx-auto px-6 pb-24" data-testid="risk-disclosure">
        <div className="rounded-xl border border-[#FF3B30]/25 bg-[#FF3B30]/[0.05] p-8 sm:p-10">
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#FF3B30]/15 text-[#FF9A94]">
              <AlertTriangle size={16} />
            </span>
            <p className="label-xs">Risk disclosure</p>
          </div>
          <p className="text-sm text-gray-300 leading-relaxed max-w-4xl">
            Trading foreign exchange and gold (XAUUSD) on margin carries a high level of risk and may not be suitable for all
            investors. The high degree of leverage can work against you as well as for you. <span className="text-white">Past performance is not
            indicative of future results.</span> ChartzFx does not guarantee any profit, return, or protection of capital, and you may
            lose some or all of your invested capital. You should only trade with money you can afford to lose. Nothing on this
            page constitutes financial, investment, legal or tax advice. ChartzFx is a non-custodial technology and account-management
            service; your funds remain in your own broker account at all times.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 pb-24">
        <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white mb-10">Frequently asked</h2>
        <div className="space-y-3">{FAQS.map((f) => <FaqItem key={f.q} {...f} />)}</div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-8">
        <div className="card-dark relative overflow-hidden p-10 sm:p-16 text-center">
          <div className="absolute inset-0 bg-gradient-to-r from-[#FFD600]/15 via-transparent to-[#7000FF]/15" />
          <h2 className="relative text-2xl sm:text-4xl tracking-tight font-medium text-white">Ready to put professional gold trading to work?</h2>
          <p className="relative text-gray-400 mt-4 text-sm sm:text-base flex items-center justify-center gap-2 flex-wrap">
            <Wallet size={15} className="text-[#FFD600]" /> Minimum $1,000 equity · 50/50 on eligible net profit · settled every Friday
          </p>
          <Link data-testid="cta-register-button" to="/register" className="relative btn-primary inline-flex px-8 py-3 mt-8 text-sm font-medium items-center gap-2">
            Get Started <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
